import { Injectable } from '@angular/core';
import { UtilsService } from '../core/utils.service';
import { environment } from 'environments/environment';
import { Subject, Observable } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ThorWebsocketService {
  private socket: WebSocket | null = null;
  private messageSubject = new Subject<any>();
  public onMessage$: Observable<any> = this.messageSubject.asObservable();

  constructor(private utils: UtilsService) { }

  /**
   * Constructs the WebSocket URL with all required query parameters.
   * @param sessionId A unique session ID (UUID)
   * @param moduleName The name of the module
   */
  buildWsUrl(sessionId: string, moduleName: string) {
    const user = this.utils.getLoggedInUser();
    const wsUri = environment.thor.wsUri;

    const authToken = encodeURIComponent(user.sessionId);
    const targetAgent = encodeURIComponent(environment.thor.targetAgent);
    const userId = encodeURIComponent(user.id.toString());
    const emailId = encodeURIComponent(user.email);

    let url = `${wsUri}?session_id=${encodeURIComponent(sessionId)}&auth_token=${authToken}&target_agent=${targetAgent}&module_name=${encodeURIComponent(moduleName)}`;

    if (userId) url += `&user_id=${userId}`;
    if (emailId) url += `&email_id=${emailId}`;

    return url;
  }

  /**
   * Establishes a new WebSocket connection.
   * @param sessionId The session ID to use for this connection.
   * @param moduleName The name of the module
   * @returns A promise that resolves when the connection is successfully opened.
   */
  connect(sessionId: string, moduleName: string): Promise<void> {
    // Close existing connection if any
    this.disconnect();

    const url = this.buildWsUrl(sessionId, moduleName);

    return new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(url);

        this.socket.onopen = () => {
          resolve();
        };

        this.socket.onmessage = (event) => {
          let data;
          try {
            data = JSON.parse(event.data);
          } catch (e) {
            data = { response: event.data };
          }
          this.messageSubject.next(data);
        };

        this.socket.onclose = (event) => {
          // If the socket closes before opening, reject the promise
          reject(new Error(`WebSocket closed during handshake: ${event.reason}`));
        };

        this.socket.onerror = (error) => {
          reject(error);
        };

      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Sends a message to the THOR engine.
   * @param payload The text query (string) or a structured object to send.
   */
  send(payload: any) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      const data = typeof payload === 'string' ? { message: payload } : payload;
      this.socket.send(JSON.stringify(data));
    } else {
      console.error('WebSocket is not open. Current state:', this.socket ? this.socket.readyState : 'null');
    }
  }

  /**
   * Returns the current readyState of the WebSocket.
   */
  getReadyState(): number {
    return this.socket ? this.socket.readyState : -1;
  }

  /**
   * Closes the WebSocket connection and cleans up.
   */
  disconnect() {
    if (this.socket) {
      // 1000 indicates a normal closure
      this.socket.close(1000, "Component destroyed or manual disconnect");
      this.socket = null;
    }
  }
}
